import React, { useState } from "react";
import { alpha, Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Info, Send, TriangleAlert } from "lucide-react";
import BadgeBoxCst from "@components/BadgeBoxCst";
import { PagesID } from "@utils/utilsConfigurations";
import EnrollmentListShell from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/EnrollmentListShell";
import { openToast } from "@utils/toastStore";
import {
  getInviteeName,
  useSessionInvitations,
} from "../hooks/useSessionInvitations";
import { EditionSession } from "../types";

interface SessionInvitesTabProps {
  corporateId: string | number;
  editionId: string | number;
  session: EditionSession;
}

const PAGE_SIZE = 10;

const STATUS_VARIANT: Record<
  string,
  "default" | "warning" | "success" | "error"
> = {
  NOT_SENT: "default",
  PENDING: "warning",
  TENTATIVE: "warning",
  ACCEPTED: "success",
  DECLINED: "error",
  NOT_FOUND: "error",
};

const STATUS_WITH_ICON = ["PENDING", "TENTATIVE", "DECLINED", "NOT_FOUND"];

const SessionInvitesTab: React.FC<SessionInvitesTabProps> = ({
  corporateId,
  editionId,
  session,
}) => {
  const { t } = useTranslation("session-detail");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const {
    items,
    totalItems,
    totalPages,
    isLoading,
    mutate,
    resend,
    isResending,
  } = useSessionInvitations({
    corporateId,
    editionId,
    sessionId: session.id,
    meetingId: session.online_meeting_id,
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    fieldFilters,
  });

  const handleSendMail = async () => {
    try {
      const res: any = await resend();
      if (res?.success === false) {
        openToast({
          severity: "error",
          message: String(t("invites.send-error")),
        });
      } else {
        openToast({
          severity: "success",
          message: String(t("invites.send-success")),
        });
        mutate();
      }
    } catch (e: any) {
      openToast({
        severity: "error",
        message: e?.message || String(t("invites.send-error")),
      });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <EnrollmentListShell
        search={search}
        onSearchSubmit={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder={String(t("invites.search-placeholder"))}
        searchButtonLabel={String(t("invites.search-button"))}
        hideSearch
        filtersPageId={PagesID["corporate.virtual-session-invitations"]}
        fieldFilters={fieldFilters}
        onApplyFilters={(next) => {
          setFieldFilters(next);
          setPage(1);
        }}
        filtersExcludeKeys={
          ["MSTEAMS", "GMEET"].includes(session.tool ?? "")
            ? undefined
            : ["role"]
        }
        filtersOptionsKeyOverrides={{
          role:
            session.tool === "MSTEAMS"
              ? "meeting_role_teams"
              : "meeting_role_gmeet",
        }}
        headerSlot={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "10px 14px",
              borderRadius: "10px",
              backgroundColor: alpha(theme.palette.info.main, 0.12),
              border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            }}
          >
            <Info size={16} color={theme.palette.info.main} />
            <Typography
              sx={{ fontSize: "12px", color: theme.palette.text.primary }}
            >
              {t("invites.banner")}
            </Typography>
          </Box>
        }
        isLoading={isLoading}
        isEmpty={items.length === 0}
        emptyLabel={String(t("invites.empty"))}
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={totalItems}
        totalPages={totalPages}
        onPageChange={setPage}
      >
        {items.map((invitee, index) => {
          const inviteeStatus = invitee.status || "";
          return (
            <Box
              key={`${invitee.email ?? "row"}-${index}`}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                padding: "12px 16px",
                borderRadius: "10px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                  }}
                >
                  {getInviteeName(invitee)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.text.tertiary,
                  }}
                >
                  {invitee.email || "-"}
                </Typography>
              </Box>
              <BadgeBoxCst
                variant={STATUS_VARIANT[inviteeStatus] || "default"}
                icon={
                  STATUS_WITH_ICON.includes(inviteeStatus) ? (
                    <TriangleAlert />
                  ) : undefined
                }
              >
                {inviteeStatus ? t(`invites.status.${inviteeStatus}`) : "-"}
              </BadgeBoxCst>
            </Box>
          );
        })}
      </EnrollmentListShell>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          endIcon={<Send size={16} />}
          onClick={handleSendMail}
          disabled={isResending}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
          }}
        >
          {t("invites.send-mail")}
        </Button>
      </Box>
    </Box>
  );
};

export default SessionInvitesTab;
